import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import SendModal from '../components/SendModal';
import { useAuth } from '../contexts/AuthContext';

// Extracted DDS components
import { 
  DDSStatsCards, 
  DDSFilters, 
  DDSEmptyState, 
  DDSCard,
  DDSDeleteDialog,
  DDSAttendanceModal 
} from '../components/dds';

import { 
  Plus, 
  Video, 
  Clock, 
  Award, 
  Play, 
  Edit, 
  Trash2, 
  Eye,
  Upload,
  Link,
  Shield,
  Heart,
  User,
  Flame,
  Zap,
  Info,
  HardHat,
  RefreshCw,
  Search,
  BarChart3,
  CheckCircle,
  Folder,
  Tag,
  AlertTriangle,
  Wrench,
  Truck,
  Clipboard,
  FileText,
  Download,
  Users,
  Calendar,
  ExternalLink,
  Instagram,
  Globe,
  MessageCircle,
  Book,
  GraduationCap,
  Star,
  Target,
  Hand,
  Brain,
  FileVideo,
  PlusCircle,
  Settings,
  ChevronDown,
  Save,
  Send,
  Mail,
  Loader2,
  Check,
  Image,
  ImagePlus,
  FileBadge,
  Signature,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  'shield': Shield,
  'heart': Heart,
  'user': User,
  'hard-hat': HardHat,
  'flame': Flame,
  'plus-circle': PlusCircle,
  'flask': Info,
  'zap': Zap,
  'brain': Brain,
  'info': Info,
  'folder': Folder,
  'file-video': FileVideo,
  'alert-triangle': AlertTriangle,
  'eye': Eye,
  'hand': Hand,
  'truck': Truck,
  'wrench': Wrench,
  'clipboard': Clipboard,
  'book': Book,
  'graduation-cap': GraduationCap,
  'star': Star,
  'target': Target,
  'tag': Tag
};

const DDSManagement = () => {
  const [ddsList, setDdsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');
  const [userRole, setUserRole] = useState('admin'); // Track user role
  
  // DDS Modal States
  const [showDDSModal, setShowDDSModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDDS, setSelectedDDS] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Category Modal States
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showProfessionalSection, setShowProfessionalSection] = useState(false);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    categoria: 'geral',
    tipo_conteudo: 'video', // 'video' ou 'texto'
    video_url: '',
    conteudo_texto: '', // Para DDS de texto
    duracao_minutos: 5,
    pontos: 50,
    // Dados do Profissional
    autor_nome: '',
    autor_especialidade: '',
    autor_link: '',
    autor_foto_url: '',
    // Flag Global (apenas superadmin)
    is_global: false
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    nome: '',
    icon: 'folder',
    cor: '#6b7280'
  });

  // Attendance States
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [generalAttendance, setGeneralAttendance] = useState(null);

  // Send to Employees States
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingDDS, setSendingDDS] = useState(null);

  // Rich Text Editor States
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ddsRes, catRes, statsRes, roleRes] = await Promise.all([
        axios.get(`${API}/api/dds`, { withCredentials: true }),
        axios.get(`${API}/api/dds/categories`, { withCredentials: true }),
        axios.get(`${API}/api/dds/stats`, { withCredentials: true }),
        axios.get(`${API}/api/users/me/role`, { withCredentials: true }).catch(() => ({ data: { role: 'admin' } }))
      ]);
      
      setDdsList(ddsRes.data || []);
      setCategories(catRes.data || []);
      setStats(statsRes.data);
      setUserRole(roleRes.data?.role || 'admin');
    } catch (error) {
      console.error('Error fetching DDS data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchGeneralAttendance();
  }, [fetchData]);

  // Open send modal for a DDS
  const openSendModal = (dds) => {
    setSendingDDS(dds);
    setShowSendModal(true);
  };

  // Handle DDS sending via SendModal - uses public token links
  const handleSendDDS = async (method, employeeIds) => {
    if (!sendingDDS || employeeIds.length === 0) return;
    
    try {
      // Send DDS with public token links (no login required for employees)
      const response = await axios.post(`${API}/api/dds/${sendingDDS.id}/send-with-token`, {
        employee_ids: employeeIds,
        method: method,
      }, { withCredentials: true });
      
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`DDS enviado com sucesso para ${result.success_count} funcionário(s)! Links públicos gerados.`);
      }
      if (result.failed_count > 0) {
        toast.warning(`Falha ao enviar para ${result.failed_count} funcionário(s)`);
      }
      
      setShowSendModal(false);
      setSendingDDS(null);
      
      // Refresh data to show updated stats
      fetchData();
      
    } catch (error) {
      console.error('Error sending DDS:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar DDS');
      throw error; // Re-throw to let SendModal handle it
    }
  };

  // ============= DDS Functions =============
  
  const handleSubmitDDS = async (e) => {
    e.preventDefault();
    
    if (!formData.titulo) {
      toast.error('Título é obrigatório');
      return;
    }
    
    // Validação baseada no tipo de conteúdo
    if (formData.tipo_conteudo === 'video' && !formData.video_url) {
      toast.error('URL do vídeo é obrigatória para DDS de vídeo');
      return;
    }
    
    if (formData.tipo_conteudo === 'texto' && !formData.conteudo_texto) {
      toast.error('Conteúdo de texto é obrigatório para DDS de texto');
      return;
    }
    
    try {
      
      
      
      if (selectedDDS) {
        await axios.patch(`${API}/api/dds/${selectedDDS.id}`, formData, { withCredentials: true });
        toast.success('DDS atualizado com sucesso!');
      } else {
        await axios.post(`${API}/api/dds`, formData, { withCredentials: true });
        toast.success('DDS criado com sucesso!');
      }
      
      setShowDDSModal(false);
      resetDDSForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar DDS');
    }
  };

  const handleDeleteDDS = async () => {
    if (!selectedDDS) return;
    
    try {
      
      await axios.delete(`${API}/api/dds/${selectedDDS.id}`, { withCredentials: true });
      
      toast.success('DDS excluído!');
      setShowDeleteDialog(false);
      setSelectedDDS(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir DDS');
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.mp4')) {
      toast.error('Apenas arquivos MP4 são permitidos');
      return;
    }
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 50MB');
      return;
    }
    
    setUploading(true);
    
    try {
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/api/dds/upload-video`, formDataUpload, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, video_url: `${API}${response.data.video_url}` }));
      toast.success(`Vídeo enviado! (${response.data.size_mb}MB)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar vídeo');
    } finally {
      setUploading(false);
    }
  };

  // Handle image upload for Markdown Editor
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato não permitido. Use: JPG, PNG, WebP ou GIF');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo: 10MB');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/api/dds/upload-image`, formDataUpload, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Get image URL - use full URL for Supabase, or construct for local
      let imageUrl = response.data.image_url;
      if (response.data.storage === 'local') {
        imageUrl = `${API}${response.data.image_url}`;
      }
      
      // Insert image markdown at the end of content
      const imageMarkdown = `\n![${file.name}](${imageUrl})\n`;
      setFormData(prev => ({
        ...prev,
        conteudo_texto: (prev.conteudo_texto || '') + imageMarkdown
      }));
      
      toast.success(`Imagem inserida! (${response.data.size_mb}MB)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle image URL insertion
  const handleInsertImageUrl = () => {
    const url = prompt('Cole a URL da imagem:');
    if (url) {
      const altText = prompt('Descrição da imagem (opcional):', 'Imagem');
      const imageMarkdown = `\n![${altText || 'Imagem'}](${url})\n`;
      setFormData(prev => ({
        ...prev,
        conteudo_texto: (prev.conteudo_texto || '') + imageMarkdown
      }));
      toast.success('Imagem inserida!');
    }
  };

  const resetDDSForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      categoria: 'geral',
      tipo_conteudo: 'video',
      video_url: '',
      conteudo_texto: '',
      duracao_minutos: 5,
      pontos: 50,
      autor_nome: '',
      autor_especialidade: '',
      autor_link: '',
      autor_foto_url: '',
      is_global: false
    });
    setSelectedDDS(null);
  };

  const openEditDDSModal = (dds) => {
    setSelectedDDS(dds);
    setFormData({
      titulo: dds.titulo,
      descricao: dds.descricao || '',
      categoria: dds.categoria || 'geral',
      tipo_conteudo: dds.tipo_conteudo || 'video',
      video_url: dds.video_url || '',
      conteudo_texto: dds.conteudo_texto || '',
      duracao_minutos: dds.duracao_minutos || 5,
      pontos: dds.pontos || 50,
      autor_nome: dds.autor_nome || '',
      autor_especialidade: dds.autor_especialidade || '',
      autor_link: dds.autor_link || '',
      autor_foto_url: dds.autor_foto_url || '',
      is_global: dds.is_global || false
    });
    setShowDDSModal(true);
  };

  // ============= Category Functions =============
  
  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    
    if (!categoryFormData.nome) {
      toast.error('Nome da categoria é obrigatório');
      return;
    }
    
    try {
      
      
      
      if (selectedCategory && !selectedCategory.system) {
        await axios.patch(`${API}/api/dds/categories/${selectedCategory.id}`, categoryFormData, { withCredentials: true });
        toast.success('Categoria atualizada!');
      } else {
        await axios.post(`${API}/api/dds/categories`, categoryFormData, { withCredentials: true });
        toast.success('Categoria criada!');
      }
      
      setShowCategoryModal(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;
    
    try {
      
      await axios.delete(`${API}/api/dds/categories/${selectedCategory.id}`, { withCredentials: true });
      
      toast.success('Categoria excluída!');
      setShowDeleteCategoryDialog(false);
      setSelectedCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
    }
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      nome: '',
      icon: 'folder',
      cor: '#6b7280'
    });
    setSelectedCategory(null);
  };

  const openEditCategoryModal = (cat) => {
    if (cat.system) {
      toast.error('Categorias padrão não podem ser editadas');
      return;
    }
    setSelectedCategory(cat);
    setCategoryFormData({
      nome: cat.nome,
      icon: cat.icon || 'folder',
      cor: cat.cor || '#6b7280'
    });
    setShowCategoryModal(true);
  };

  // ============= Attendance Functions =============

  const fetchAttendance = async (ddsId) => {
    setLoadingAttendance(true);
    try {
      
      const response = await axios.get(`${API}/api/dds/${ddsId}/presenca`, { withCredentials: true });
      setAttendanceData(response.data);
      setShowAttendanceModal(true);
    } catch (error) {
      toast.error('Erro ao carregar lista de presença');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchGeneralAttendance = async () => {
    try {
      
      const response = await axios.get(`${API}/api/dds/presenca/geral`, { withCredentials: true });
      setGeneralAttendance(response.data);
    } catch (error) {
      console.error('Error fetching general attendance:', error);
    }
  };

  const exportAttendancePDF = async (ddsId) => {
    try {
      
      const response = await axios.get(`${API}/api/dds/${ddsId}/presenca/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lista_presenca_dds_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const exportGeneralAttendancePDF = async () => {
    try {
      
      const response = await axios.get(`${API}/api/dds/presenca/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_participacao_dds_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório PDF exportado!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  // ============= Helper Functions =============
  
  const getCategoryIcon = (iconName) => {
    const Icon = ICON_MAP[iconName] || Folder;
    return Icon;
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.cor || '#6b7280';
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.nome || categoryId;
  };

  const getVideoPreview = (dds) => {
    if (!dds.video_info) return null;
    if (dds.video_info.type === 'youtube' && dds.video_info.thumbnail) {
      return dds.video_info.thumbnail;
    }
    return null;
  };

  const filteredDDS = ddsList.filter(dds => {
    const matchesCategory = filterCategory === 'all' || dds.categoria === filterCategory;
    const matchesSearch = !searchTerm || 
      dds.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dds.descricao && dds.descricao.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const customCategories = categories.filter(c => !c.system);
  const systemCategories = categories.filter(c => c.system);

  const availableIcons = Object.keys(ICON_MAP);

  return (
    <div className="space-y-6" data-testid="dds-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            DDS - Diálogo Diário de Segurança
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Gerencie vídeos e textos curtos de segurança (até 5 minutos)
          </p>
        </div>
      </div>

      {/* Stats Cards - Using extracted component */}
      <DDSStatsCards stats={stats} categoriesCount={categories.length} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Conteúdos
          </TabsTrigger>
          <TabsTrigger value="presenca" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Presença
          </TabsTrigger>
          <TabsTrigger value="categorias" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Categorias
          </TabsTrigger>
        </TabsList>

        {/* Videos Tab */}
        <TabsContent value="videos" className="space-y-4">
          {/* Filters */}
          <Card className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar DDS..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-dds"
                  />
                </div>
              </div>
              
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[200px]" data-testid="filter-category">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
              
              <Button 
                onClick={() => { resetDDSForm(); setShowDDSModal(true); }}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="add-dds-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar DDS
              </Button>
            </div>
          </Card>

          {/* DDS List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : filteredDDS.length === 0 ? (
            <Card className="p-12 text-center">
              <Video className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">
                Nenhum DDS encontrado
              </h3>
              <p className="text-slate-500 mb-4">
                Adicione seu primeiro Diálogo Diário de Segurança
              </p>
              <Button 
                onClick={() => { resetDDSForm(); setShowDDSModal(true); }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar DDS
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDDS.map(dds => {
                const CategoryIcon = getCategoryIcon(categories.find(c => c.id === dds.categoria)?.icon || 'info');
                return (
                  <Card 
                    key={dds.id} 
                    className={`overflow-hidden hover:shadow-lg transition-shadow ${!dds.ativo ? 'opacity-60' : ''}`}
                    data-testid={`dds-card-${dds.id}`}
                  >
                    {/* Video Thumbnail */}
                    <div className="relative bg-slate-900 aspect-video flex items-center justify-center">
                      {getVideoPreview(dds) ? (
                        <img 
                          src={getVideoPreview(dds)} 
                          alt={dds.titulo}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Video className="w-16 h-16 text-slate-600" />
                      )}
                      
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                      
                      {/* Duration badge */}
                      <div className="absolute bottom-2 right-2">
                        <Badge className="bg-black/70 text-white">
                          <Clock className="w-3 h-3 mr-1" />
                          {dds.duracao_minutos} min
                        </Badge>
                      </div>
                      
                      {/* Content Type badge */}
                      <div className="absolute top-2 right-2">
                        <Badge className={dds.tipo_conteudo === 'texto' ? 'bg-purple-600' : 'bg-red-600'}>
                          {dds.tipo_conteudo === 'texto' ? (
                            <><FileText className="w-3 h-3 mr-1" />Texto</>
                          ) : (
                            <><Video className="w-3 h-3 mr-1" />Vídeo</>
                          )}
                        </Badge>
                      </div>
                      
                      {/* Category badge */}
                      <div className="absolute top-2 left-2">
                        <Badge style={{ backgroundColor: getCategoryColor(dds.categoria) }}>
                          <CategoryIcon className="w-3 h-3 mr-1" />
                          {getCategoryName(dds.categoria)}
                        </Badge>
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg line-clamp-2">{dds.titulo}</h3>
                        {dds.is_global && (
                          <Badge className="bg-blue-500 text-white text-xs flex-shrink-0">
                            <Globe className="w-3 h-3 mr-1" />
                            GLOBAL
                          </Badge>
                        )}
                      </div>
                      
                      {dds.descricao && (
                        <p className="text-sm text-slate-500 mb-3 line-clamp-2">{dds.descricao}</p>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3 text-slate-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {dds.total_visualizacoes || 0}
                          </span>
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            <Award className="w-3 h-3 mr-1" />
                            +{dds.pontos} pts
                          </Badge>
                        </div>
                        
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#0d9488] hover:text-[#0b7d73] hover:bg-teal-50"
                            onClick={() => openSendModal(dds)}
                            title="Enviar para funcionários"
                            data-testid={`send-dds-${dds.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDDSModal(dds)}
                            data-testid={`edit-dds-${dds.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-700"
                            onClick={() => fetchAttendance(dds.id)}
                            title="Ver lista de presença"
                            data-testid={`attendance-dds-${dds.id}`}
                          >
                            <Users className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => { setSelectedDDS(dds); setShowDeleteDialog(true); }}
                            data-testid={`delete-dds-${dds.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="presenca" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Lista de Presença</h3>
              <p className="text-slate-500">Acompanhe a participação dos funcionários nos DDS</p>
            </div>
            <Button 
              onClick={exportGeneralAttendancePDF}
              className="bg-green-600 hover:bg-green-700"
              data-testid="export-general-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Exportar Relatório Geral (PDF)
            </Button>
          </div>

          {/* General Stats */}
          {generalAttendance && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="text-center">
                  <Video className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-bold">{generalAttendance.total_dds}</p>
                  <p className="text-sm text-slate-500">Total de DDS</p>
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-center">
                  <Users className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold">{generalAttendance.total_funcionarios}</p>
                  <p className="text-sm text-slate-500">Funcionários</p>
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold">{generalAttendance.total_conclusoes}</p>
                  <p className="text-sm text-slate-500">Total Conclusões</p>
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-center">
                  <BarChart3 className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                  <p className="text-2xl font-bold">{generalAttendance.media_conclusoes_por_funcionario}</p>
                  <p className="text-sm text-slate-500">Média por Funcionário</p>
                </div>
              </Card>
            </div>
          )}

          {/* DDS List with Attendance */}
          <Card>
            <div className="p-4 border-b">
              <h4 className="font-semibold">DDS Cadastrados - Clique para ver presença</h4>
            </div>
            <div className="divide-y">
              {ddsList.map(dds => {
                const CategoryIcon = getCategoryIcon(categories.find(c => c.id === dds.categoria)?.icon || 'info');
                return (
                  <div 
                    key={dds.id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                    onClick={() => fetchAttendance(dds.id)}
                    data-testid={`dds-attendance-row-${dds.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: getCategoryColor(dds.categoria) + '20' }}
                      >
                        <CategoryIcon className="w-5 h-5" style={{ color: getCategoryColor(dds.categoria) }} />
                      </div>
                      <div>
                        <h5 className="font-medium">{dds.titulo}</h5>
                        <p className="text-sm text-slate-500">
                          {getCategoryName(dds.categoria)} | {dds.duracao_minutos} min | +{dds.pontos} pts
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">{dds.total_visualizacoes || 0}</p>
                        <p className="text-xs text-slate-500">presentes</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); exportAttendancePDF(dds.id); }}
                        data-testid={`export-pdf-${dds.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </div>
                );
              })}
              
              {ddsList.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Nenhum DDS cadastrado ainda.
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categorias" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-600 dark:text-slate-400">
              Gerencie as categorias de DDS da sua empresa
            </p>
            <Button 
              onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Categoria
            </Button>
          </div>

          {/* Custom Categories */}
          {customCategories.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-500" />
                Suas Categorias Personalizadas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customCategories.map(cat => {
                  const Icon = getCategoryIcon(cat.icon);
                  const ddsCount = stats?.por_categoria?.[cat.id] || 0;
                  return (
                    <Card key={cat.id} className="p-4" data-testid={`category-card-${cat.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: cat.cor + '20' }}
                          >
                            <Icon className="w-6 h-6" style={{ color: cat.cor }} />
                          </div>
                          <div>
                            <h4 className="font-semibold">{cat.nome}</h4>
                            <p className="text-sm text-slate-500">{ddsCount} DDS</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditCategoryModal(cat)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => { setSelectedCategory(cat); setShowDeleteCategoryDialog(true); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* System Categories */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Categorias Padrão do Sistema
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Estas categorias são padrão e não podem ser editadas ou excluídas.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {systemCategories.map(cat => {
                const Icon = getCategoryIcon(cat.icon);
                const ddsCount = stats?.por_categoria?.[cat.id] || 0;
                return (
                  <Card key={cat.id} className="p-3 opacity-80">
                    <div className="flex items-center gap-2">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: cat.cor + '20' }}
                      >
                        <Icon className="w-4 h-4" style={{ color: cat.cor }} />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium">{cat.nome}</h4>
                        <p className="text-xs text-slate-400">{ddsCount} DDS</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* DDS Create/Edit Modal - Compact Version */}
      <Dialog open={showDDSModal} onOpenChange={setShowDDSModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Video className="w-4 h-4 text-amber-500" />
              {selectedDDS ? 'Editar DDS' : 'Novo DDS'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitDDS} className="space-y-3">
            {/* Título */}
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Título *</label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ex: Uso correto de EPIs"
                className="h-9"
                data-testid="dds-titulo"
              />
            </div>
            
            {/* Grid: Categoria + Duração + Pontos */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Categoria</label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger className="h-9" data-testid="dds-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => {
                      const Icon = getCategoryIcon(cat.icon);
                      return (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-1.5">
                            <Icon className="w-3 h-3" style={{ color: cat.cor }} />
                            <span className="text-sm">{cat.nome}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Duração (min)</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.duracao_minutos}
                  onChange={(e) => setFormData({ ...formData, duracao_minutos: parseInt(e.target.value) || 5 })}
                  className="h-9"
                  data-testid="dds-duracao"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Pontos</label>
                <Input
                  type="number"
                  min="10"
                  max="200"
                  value={formData.pontos}
                  onChange={(e) => setFormData({ ...formData, pontos: parseInt(e.target.value) || 50 })}
                  className="h-9"
                  data-testid="dds-pontos"
                />
              </div>
            </div>
            
            {/* Descrição - Compacta */}
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Descrição</label>
              <Textarea
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Breve descrição do conteúdo..."
                rows={2}
                className="resize-none text-sm"
                data-testid="dds-descricao"
              />
            </div>
            
            {/* Tipo de Conteúdo */}
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">Tipo de Conteúdo *</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.tipo_conteudo === 'video' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData({ ...formData, tipo_conteudo: 'video' })}
                  className={formData.tipo_conteudo === 'video' ? 'bg-[#0d9488] hover:bg-[#0b7d73]' : ''}
                  data-testid="dds-tipo-video"
                >
                  <Video className="w-4 h-4 mr-1.5" />
                  Vídeo
                </Button>
                <Button
                  type="button"
                  variant={formData.tipo_conteudo === 'texto' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData({ ...formData, tipo_conteudo: 'texto' })}
                  className={formData.tipo_conteudo === 'texto' ? 'bg-[#0d9488] hover:bg-[#0b7d73]' : ''}
                  data-testid="dds-tipo-texto"
                >
                  <FileText className="w-4 h-4 mr-1.5" />
                  Texto
                </Button>
              </div>
            </div>

            {/* Conteúdo Condicional - Vídeo */}
            {formData.tipo_conteudo === 'video' && (
              <>
                {/* URL do Vídeo */}
                <div>
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-400">URL do Vídeo (YouTube/Vimeo) *</label>
                  <div className="relative">
                    <Link className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="pl-8 h-9 text-sm"
                      data-testid="dds-video-url"
                    />
                  </div>
                </div>
                
                {/* Upload MP4 - Compacto */}
                <div className="flex items-center gap-2 p-2 border border-dashed rounded-md bg-slate-50 dark:bg-slate-900">
                  <Upload className="w-4 h-4 text-slate-400" />
                  <label className="flex-1 text-xs text-slate-500 cursor-pointer">
                    {uploading ? 'Enviando...' : 'Ou upload MP4 (máx. 50MB)'}
                    <input
                      type="file"
                      accept="video/mp4"
                      className="hidden"
                      onChange={handleVideoUpload}
                      disabled={uploading}
                      data-testid="dds-upload-input"
                    />
                  </label>
                </div>
              </>
            )}

            {/* Conteúdo Condicional - Texto com Editor Rico */}
            {formData.tipo_conteudo === 'texto' && (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                      Conteúdo do DDS (com formatação) *
                    </label>
                    <div className="flex items-center gap-1">
                      {/* Upload Image Button */}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          data-testid="dds-image-upload"
                        />
                        <span 
                          className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded border transition-colors ${
                            uploadingImage 
                              ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                              : 'bg-purple-50 text-purple-600 border-purple-200 hover:bg-purple-100'
                          }`}
                        >
                          {uploadingImage ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Upload className="w-3 h-3" />
                          )}
                          {uploadingImage ? 'Enviando...' : 'Upload Imagem'}
                        </span>
                      </label>
                      {/* Insert URL Button */}
                      <button
                        type="button"
                        onClick={handleInsertImageUrl}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded border bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 transition-colors"
                        data-testid="dds-image-url-btn"
                      >
                        <ImagePlus className="w-3 h-3" />
                        URL Imagem
                      </button>
                    </div>
                  </div>
                  
                  {/* Markdown Editor */}
                  <div data-color-mode="light" data-testid="dds-rich-editor">
                    <MDEditor
                      value={formData.conteudo_texto}
                      onChange={(value) => setFormData({ ...formData, conteudo_texto: value || '' })}
                      preview="edit"
                      height={280}
                      textareaProps={{
                        placeholder: `Escreva o conteúdo do DDS aqui...

Use a barra de ferramentas para:
• **negrito**, *itálico*, ~~riscado~~
• Criar listas com - ou 1.
• Inserir imagens com ![alt](url)
• Adicionar links com [texto](url)`
                      }}
                    />
                  </div>
                  
                  {/* Helper text */}
                  <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1">
                    <span>
                      Use **negrito** | *itálico* | ![imagem](url) | [link](url)
                    </span>
                    <span>
                      {(formData.conteudo_texto || '').length} caracteres
                    </span>
                  </div>
                </div>
                
                {/* Image upload tip card */}
                <div className="flex items-start gap-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-md border border-purple-200 dark:border-purple-800">
                  <Image className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div className="text-[10px] text-purple-700 dark:text-purple-300">
                    <strong>Dica:</strong> Para imagens grandes, use o botão "Upload Imagem" que envia para o servidor. 
                    Para imagens externas, use "URL Imagem". Formatos aceitos: JPG, PNG, WebP, GIF (máx 10MB).
                  </div>
                </div>
              </div>
            )}

            {/* Acordeão: Dados do Profissional */}
            <div className="border rounded-md overflow-hidden">
              <button
                type="button"
                onClick={() => setShowProfessionalSection(!showProfessionalSection)}
                className="w-full flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-purple-500" />
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    Destaque do Profissional
                  </span>
                  {formData.autor_nome && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-purple-600 border-purple-300">
                      Preenchido
                    </Badge>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showProfessionalSection ? 'rotate-180' : ''}`} />
              </button>
              
              {showProfessionalSection && (
                <div className="p-3 space-y-2 bg-white dark:bg-slate-900">
                  <p className="text-[10px] text-slate-500">Card de marketing exibido ao funcionário</p>
                  
                  {/* Grid: Nome + Especialidade */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500">Nome</label>
                      <Input
                        value={formData.autor_nome}
                        onChange={(e) => setFormData({ ...formData, autor_nome: e.target.value })}
                        placeholder="Dr. João Silva"
                        className="h-8 text-sm"
                        data-testid="dds-autor-nome"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Especialidade</label>
                      <Input
                        value={formData.autor_especialidade}
                        onChange={(e) => setFormData({ ...formData, autor_especialidade: e.target.value })}
                        placeholder="Eng. Segurança"
                        className="h-8 text-sm"
                        data-testid="dds-autor-especialidade"
                      />
                    </div>
                  </div>
                  
                  {/* Grid: Link + Foto */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500">Link de Contato</label>
                      <Input
                        value={formData.autor_link}
                        onChange={(e) => setFormData({ ...formData, autor_link: e.target.value })}
                        placeholder="https://wa.me/..."
                        className="h-8 text-sm"
                        data-testid="dds-autor-link"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Foto (URL)</label>
                      <Input
                        value={formData.autor_foto_url}
                        onChange={(e) => setFormData({ ...formData, autor_foto_url: e.target.value })}
                        placeholder="https://..."
                        className="h-8 text-sm"
                        data-testid="dds-autor-foto"
                      />
                    </div>
                  </div>

                  {/* Preview Compacto */}
                  {formData.autor_nome && (
                    <div className="flex items-center gap-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-100 dark:border-purple-800">
                      <div className="w-8 h-8 rounded-full bg-purple-200 dark:bg-purple-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {formData.autor_foto_url ? (
                          <img src={formData.autor_foto_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800 dark:text-white truncate">{formData.autor_nome}</p>
                        <p className="text-[10px] text-slate-500 truncate">{formData.autor_especialidade || 'Especialidade'}</p>
                      </div>
                      {formData.autor_link && <ExternalLink className="w-3 h-3 text-purple-500 flex-shrink-0" />}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Seção Conteúdo Global - Apenas para Superadmin (Compacta) */}
            {userRole === 'superadmin' && (
              <div className="flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-950/30 rounded-md border border-blue-200 dark:border-blue-800">
                <input
                  type="checkbox"
                  id="is_global"
                  checked={formData.is_global}
                  onChange={(e) => setFormData({ ...formData, is_global: e.target.checked })}
                  className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  data-testid="is-global-checkbox"
                />
                <label htmlFor="is_global" className="flex-1 cursor-pointer">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-blue-800 dark:text-blue-300">
                    <Globe className="w-3.5 h-3.5" />
                    Conteúdo Global
                  </span>
                  <p className="text-[10px] text-blue-600 dark:text-blue-400">
                    Visível para todas as empresas
                  </p>
                </label>
              </div>
            )}
            
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDDSModal(false)} size="sm">
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" size="sm" data-testid="save-dds-btn">
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {selectedDDS ? 'Salvar' : 'Criar DDS'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Create/Edit Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-500" />
              {selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitCategory} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Nome da Categoria *
              </label>
              <Input
                value={categoryFormData.nome}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, nome: e.target.value })}
                placeholder="Ex: Trabalho em Altura"
                data-testid="category-nome"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Ícone
              </label>
              <Select 
                value={categoryFormData.icon} 
                onValueChange={(value) => setCategoryFormData({ ...categoryFormData, icon: value })}
              >
                <SelectTrigger data-testid="category-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableIcons.map(iconName => {
                    const Icon = ICON_MAP[iconName];
                    return (
                      <SelectItem key={iconName} value={iconName}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {iconName}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Cor
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="color"
                  value={categoryFormData.cor}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, cor: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                  data-testid="category-cor"
                />
                <Input
                  value={categoryFormData.cor}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, cor: e.target.value })}
                  placeholder="#6b7280"
                  className="flex-1"
                />
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-xs text-slate-500 mb-2">Preview:</p>
              <div className="flex items-center gap-3">
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: categoryFormData.cor + '20' }}
                >
                  {(() => {
                    const Icon = getCategoryIcon(categoryFormData.icon);
                    return <Icon className="w-6 h-6" style={{ color: categoryFormData.cor }} />;
                  })()}
                </div>
                <span className="font-medium">{categoryFormData.nome || 'Nome da Categoria'}</span>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="save-category-btn">
                {selectedCategory ? 'Salvar' : 'Criar Categoria'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete DDS Confirmation */}
      <DDSDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Excluir DDS?"
        itemName={selectedDDS?.titulo}
        onConfirm={handleDeleteDDS}
      />

      {/* Delete Category Confirmation */}
      <DDSDeleteDialog
        open={showDeleteCategoryDialog}
        onOpenChange={setShowDeleteCategoryDialog}
        title="Excluir Categoria?"
        itemName={selectedCategory?.nome}
        description={`Tem certeza que deseja excluir a categoria "${selectedCategory?.nome}"? Certifique-se de que não há DDS usando esta categoria antes de excluí-la.`}
        onConfirm={handleDeleteCategory}
      />

      {/* Attendance Modal */}
      <DDSAttendanceModal
        open={showAttendanceModal}
        onOpenChange={setShowAttendanceModal}
        attendanceData={attendanceData}
        loading={loadingAttendance}
        onExportPDF={() => exportAttendancePDF(attendanceData?.dds?.id)}
      />

      {/* Send DDS Modal - Using shared component */}
      <SendModal
        open={showSendModal}
        onOpenChange={setShowSendModal}
        title="Enviar DDS para Funcionários"
        itemTitle={sendingDDS?.titulo}
        itemDescription={sendingDDS?.descricao || `Categoria: ${sendingDDS?.categoria_nome || 'Geral'}`}
        onSend={handleSendDDS}
        showMethodSelector={true}
        defaultMethod="both"
      />
    </div>
  );
};

export default DDSManagement;
